import { FC, useState, useMemo } from "react";
import {
	Typography,
	Box,
	Card,
	CardContent,
	Button,
	TextField,
	Grid,
	Paper,
	IconButton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TablePagination,
	FormControl,
	InputLabel,
	Select,
	MenuItem,
	Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import {
	useAnalytes,
	useCreateAnalyte,
	useUpdateAnalyte,
	useDeleteAnalyte,
	AnalyteRequest,
	AnalyteUpdateRequest,
} from "../../hooks/useAnalytes";
import {
	IAnalyte,
	AnalyteType,
	AnalyteStatus,
} from "../../domain/analyte.interface";
import { useUserStore } from "../../stores/user.store";
import GenericAlertModal from "../../components/modals/GenericAlertModal";

const AnalytesManagement: FC = () => {
	const { userInfo } = useUserStore();
	const [showCreateForm, setShowCreateForm] = useState(false);
	const [editingAnalyte, setEditingAnalyte] = useState<IAnalyte | null>(null);
	const [search, setSearch] = useState("");
	const [page, setPage] = useState(0);
	const [pageSize, setPageSize] = useState(10);
	const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
	const [analyteToDelete, setAnalyteToDelete] = useState<string | null>(null);

	const [formData, setFormData] = useState<Omit<AnalyteRequest, "createdBy">>({
		accountId: process.env.REACT_APP_ACCOUNT_ID || "",
		shortname: "",
		shortnameDisplay: "",
		scientificName: "",
		scientificDisplay: "",
		type: AnalyteType.OTHER_SUBSTANCE,
		status: AnalyteStatus.ACTIVE,
	});

	const { data: analytesResponse, isLoading, error } = useAnalytes();
	const createAnalyte = useCreateAnalyte();
	const updateAnalyte = useUpdateAnalyte();
	const deleteAnalyte = useDeleteAnalyte();

	const analytes = analytesResponse?.body || [];

	// Filter analytes based on search
	const filteredAnalytes = useMemo(
		() =>
			analytes.filter(
				(analyte: IAnalyte) =>
					analyte.shortname.toLowerCase().includes(search.toLowerCase()) ||
					analyte.scientificName.toLowerCase().includes(search.toLowerCase()) ||
					analyte.shortnameDisplay
						.toLowerCase()
						.includes(search.toLowerCase()) ||
					analyte.scientificDisplay
						.toLowerCase()
						.includes(search.toLowerCase()),
			),
		[analytes, search],
	);

	// Paginate analytes
	const paginatedAnalytes = useMemo(
		() => filteredAnalytes.slice(page * pageSize, (page + 1) * pageSize),
		[filteredAnalytes, page, pageSize],
	);

	const handleCreateAnalyte = async () => {
		if (!formData.shortname.trim() || !formData.scientificName.trim()) {
			alert(
				"Please fill in all required fields: Analyte Shortname and Scientific Name",
			);
			return;
		}

		try {
			const requestData: AnalyteRequest = {
				...formData,
				createdBy: userInfo?.id || "SYSTEM_USER",
			};
			await createAnalyte.mutateAsync(requestData);
			resetForm();
			setShowCreateForm(false);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error creating analyte:", error);
			alert("Failed to create analyte");
		}
	};

	const handleUpdateAnalyte = async () => {
		if (
			!editingAnalyte ||
			!formData.shortname.trim() ||
			!formData.scientificName.trim()
		) {
			alert(
				"Please fill in all required fields: Analyte Shortname and Scientific Name",
			);
			return;
		}

		try {
			const requestData: AnalyteUpdateRequest = {
				...formData,
				_id: editingAnalyte._id,
				updatedBy: userInfo?.id || "SYSTEM_USER",
			};
			await updateAnalyte.mutateAsync(requestData);
			resetForm();
			setEditingAnalyte(null);
			setShowCreateForm(false);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error updating analyte:", error);
			alert("Failed to update analyte");
		}
	};

	const handleDeleteAnalyte = async () => {
		if (!analyteToDelete) return;
		try {
			await deleteAnalyte.mutateAsync(analyteToDelete);
			setIsDeleteDialogOpen(false);
			setAnalyteToDelete(null);
		} catch (error) {
			// eslint-disable-next-line no-console
			console.error("Error deleting analyte:", error);
			alert("Failed to delete analyte");
			setIsDeleteDialogOpen(false);
		}
	};

	const handleEdit = (analyte: IAnalyte) => {
		setEditingAnalyte(analyte);
		setFormData({
			accountId: analyte.accountId,
			shortname: analyte.shortname,
			shortnameDisplay: analyte.shortnameDisplay,
			scientificName: analyte.scientificName,
			scientificDisplay: analyte.scientificDisplay,
			type: analyte.type,
			status: analyte.status,
		});
		setShowCreateForm(true);
	};

	const resetForm = () => {
		setFormData({
			accountId: process.env.REACT_APP_ACCOUNT_ID || "",
			shortname: "",
			shortnameDisplay: "",
			scientificName: "",
			scientificDisplay: "",
			type: AnalyteType.OTHER_SUBSTANCE,
			status: AnalyteStatus.ACTIVE,
		});
	};

	const handleCancel = () => {
		resetForm();
		setShowCreateForm(false);
		setEditingAnalyte(null);
	};

	if (error) {
		return (
			<Box sx={{ p: 3 }}>
				<Typography color="error">Error loading analytes</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ p: 3 }}>
			<Typography variant="h4" gutterBottom>
				Analytes Management
			</Typography>

			{/* Search and Create Button */}
			<Box sx={{ mb: 3, display: "flex", gap: 2 }}>
				<TextField
					placeholder="Search analytes..."
					value={search}
					onChange={(e) => setSearch(e.target.value)}
					InputProps={{
						startAdornment: (
							<SearchIcon sx={{ mr: 1, color: "action.active" }} />
						),
					}}
					sx={{ flexGrow: 1 }}
				/>
				<Button
					variant="contained"
					startIcon={<AddIcon />}
					onClick={() => setShowCreateForm(!showCreateForm)}
				>
					{showCreateForm ? "Cancel" : "Create Analyte"}
				</Button>
			</Box>

			{/* Create/Edit Form */}
			{showCreateForm && (
				<Card sx={{ mb: 3 }}>
					<CardContent>
						<Typography variant="h6" gutterBottom>
							{editingAnalyte ? "Edit Analyte" : "Create New Analyte"}
						</Typography>
						<Grid container spacing={2}>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Analyte Shortname *"
									value={formData.shortname}
									onChange={(e) =>
										setFormData({
											...formData,
											shortname: e.target.value,
										})
									}
									placeholder="e.g., AMP"
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Shortname Display"
									value={formData.shortnameDisplay}
									onChange={(e) =>
										setFormData({
											...formData,
											shortnameDisplay: e.target.value,
										})
									}
									placeholder="e.g., Amphetamines"
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Scientific Name *"
									value={formData.scientificName}
									onChange={(e) =>
										setFormData({
											...formData,
											scientificName: e.target.value,
										})
									}
									placeholder="e.g., Amphetamine"
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<TextField
									fullWidth
									label="Scientific Display"
									value={formData.scientificDisplay}
									onChange={(e) =>
										setFormData({
											...formData,
											scientificDisplay: e.target.value,
										})
									}
									placeholder="e.g., Amphetamine (AMP)"
								/>
							</Grid>
							<Grid item xs={12} md={6}>
								<FormControl fullWidth>
									<InputLabel>Type *</InputLabel>
									<Select
										value={formData.type}
										label="Type *"
										onChange={(e) =>
											setFormData({
												...formData,
												type: e.target.value as AnalyteType,
											})
										}
									>
										<MenuItem value={AnalyteType.STIMULANT}>
											{AnalyteType.STIMULANT}
										</MenuItem>
										<MenuItem value={AnalyteType.OTHER_SUBSTANCE}>
											{AnalyteType.OTHER_SUBSTANCE}
										</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12} md={6}>
								<FormControl fullWidth>
									<InputLabel>Status *</InputLabel>
									<Select
										value={formData.status}
										label="Status *"
										onChange={(e) =>
											setFormData({
												...formData,
												status: e.target.value as AnalyteStatus,
											})
										}
									>
										<MenuItem value={AnalyteStatus.ACTIVE}>
											{AnalyteStatus.ACTIVE}
										</MenuItem>
										<MenuItem value={AnalyteStatus.INACTIVE}>
											{AnalyteStatus.INACTIVE}
										</MenuItem>
									</Select>
								</FormControl>
							</Grid>
							<Grid item xs={12}>
								<Box sx={{ display: "flex", gap: 2 }}>
									<Button
										variant="contained"
										color="success"
										onClick={
											editingAnalyte ? handleUpdateAnalyte : handleCreateAnalyte
										}
										disabled={
											createAnalyte.isPending || updateAnalyte.isPending
										}
									>
										{editingAnalyte ? "Update" : "Create"}
									</Button>
									<Button variant="outlined" onClick={handleCancel}>
										Cancel
									</Button>
								</Box>
							</Grid>
						</Grid>
					</CardContent>
				</Card>
			)}

			{/* Analytes Table */}
			<Paper>
				<TableContainer>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Shortname</TableCell>
								<TableCell>Display Name</TableCell>
								<TableCell>Scientific Name</TableCell>
								<TableCell>Scientific Display</TableCell>
								<TableCell>Type</TableCell> <TableCell>Status</TableCell>{" "}
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{isLoading ? (
								<TableRow>
									<TableCell colSpan={7} align="center">
										Loading...
									</TableCell>
								</TableRow>
							) : paginatedAnalytes.length === 0 ? (
								<TableRow>
									<TableCell colSpan={7} align="center">
										No analytes found
									</TableCell>
								</TableRow>
							) : (
								paginatedAnalytes.map((analyte) => (
									<TableRow key={analyte._id}>
										<TableCell>{analyte.shortname}</TableCell>
										<TableCell>{analyte.shortnameDisplay}</TableCell>
										<TableCell>{analyte.scientificName}</TableCell>
										<TableCell>{analyte.scientificDisplay}</TableCell>
										<TableCell>{analyte.type}</TableCell>
										<TableCell>
											<Chip
												label={analyte.status}
												size="small"
												color={
													analyte.status === AnalyteStatus.ACTIVE
														? "success"
														: "default"
												}
											/>
										</TableCell>
										<TableCell>
											<IconButton
												size="small"
												onClick={() => handleEdit(analyte)}
												color="primary"
											>
												<EditIcon />
											</IconButton>
											<IconButton
												size="small"
												onClick={() => {
													setAnalyteToDelete(analyte._id);
													setIsDeleteDialogOpen(true);
												}}
												color="error"
											>
												<DeleteIcon />
											</IconButton>
										</TableCell>
									</TableRow>
								))
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={filteredAnalytes.length}
					page={page}
					onPageChange={(_, newPage) => setPage(newPage)}
					rowsPerPage={pageSize}
					onRowsPerPageChange={(e) => {
						setPageSize(parseInt(e.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[5, 10, 25, 50]}
				/>
			</Paper>

			{isDeleteDialogOpen && (
				<GenericAlertModal
					title="Delete Analyte"
					description="Are you sure you want to delete this registry?"
					submitText="Confirm"
					cancelText="Cancel"
					isLoading={deleteAnalyte.isPending}
					onClick={handleDeleteAnalyte}
					onCancel={() => {
						setIsDeleteDialogOpen(false);
						setAnalyteToDelete(null);
					}}
				/>
			)}
		</Box>
	);
};
export default AnalytesManagement;
